import Papa, { ParseLocalConfig, ParseResult } from 'papaparse'
import { createCsvTextStream } from './csvTextStream'

export interface ParseStreamOptions {
    delimiter?: string
    chunkSize?: number
    onChunk?: (rows: Record<string, string>[], meta: { rowsSoFar: number; delimiter: string; header: string[]; renamedHeaders?: Record<string, string> }) => void
    onError?: (message: string) => void
    onComplete?: (meta: { rowsSoFar: number; delimiter: string; header: string[] }) => void
    maxRows?: number
    encoding?: string
}

function firstDefined<T>(...vals: (T | undefined)[]): T | undefined {
    return vals.find((v) => v !== undefined)
}

// Streams the CSV via Papa Parse. Callers receive rows in chunk callbacks so
// the full dataset never has to sit in memory at once. Auto-detects the
// delimiter when `delimiter` is not provided.
export function parseCsvStream(file: File, options: ParseStreamOptions = {}): Promise<{ header: string[]; rowsSoFar: number; delimiter: string }> {
    return new Promise((resolve, reject) => {
        let header: string[] = []
        let rowsSoFar = 0
        let detectedDelimiter = firstDefined(options.delimiter, '') || ''
        const chunkSize = options.chunkSize ?? 1024 * 1024
        if (!Number.isSafeInteger(chunkSize) || chunkSize <= 0) {
            reject(new Error('CSV chunk size must be a positive integer'))
            return
        }
        const stream = createCsvTextStream(file, chunkSize, options.encoding)

        const config: ParseLocalConfig<Record<string, string>, NodeJS.ReadableStream> = {
            header: true,
            skipEmptyLines: 'greedy',
            worker: false,
            delimiter: options.delimiter,
            chunkSize,
            chunk: (results: ParseResult<Record<string, string>>, parser) => {
                if (!detectedDelimiter && results.meta.delimiter) detectedDelimiter = results.meta.delimiter
                if (header.length === 0 && results.meta.fields) header = results.meta.fields
                const rows = results.data
                rowsSoFar += rows.length
                options.onChunk?.(rows, { rowsSoFar, delimiter: detectedDelimiter, header, renamedHeaders: results.meta.renamedHeaders })
                if (options.maxRows && rowsSoFar >= options.maxRows) {
                    parser.abort()
                }
            },
            complete: () => {
                stream.cancel()
                options.onComplete?.({ rowsSoFar, delimiter: detectedDelimiter, header })
                resolve({ header, rowsSoFar, delimiter: detectedDelimiter })
            },
            error: (err: Error) => {
                stream.cancel()
                options.onError?.(err.message)
                reject(err)
            },
        }

        // File input makes Papa decode each byte slice independently with
        // FileReader.readAsText. Feed already-decoded strings through its
        // readable-stream API so UTF-8 state survives between byte chunks.
        Papa.parse(stream as unknown as NodeJS.ReadableStream, config)
        void stream.start()
    })
}

// Reads a small text prefix from the file and returns it as raw lines for
// delimiter detection. Uses a 64 KB slice so it stays cheap even for very
// large files.
export async function readSampleLines(file: File, maxBytes = 64 * 1024, maxLines = 20, encoding = 'utf-8'): Promise<string[]> {
    const slice = file.slice(0, Math.min(maxBytes, file.size))
    const decoder = new TextDecoder(encoding, { fatal: true })
    const text = decoder.decode(await slice.arrayBuffer(), { stream: slice.size < file.size })
    return text.split(/\r?\n/).slice(0, maxLines).filter(Boolean)
}
